import { HStack, VStack } from '@chakra-ui/react';
import { AutoResizeTextarea } from 'Atoms';
import { MenuSelector } from 'Molecules/Menu/MenuSelector';
import { MetricsTableData } from 'containers/Esrs/pieces/DisclosureRequirements/Metrics';
import { TimePeriodsEnums } from 'containers/Esrs/pieces/DisclosureRequirements/Requirement';
import { debounce } from 'lodash';
import { useMemo, useState, useEffect } from 'react';
import { useGetDatapointValues } from '../../QuantitativeInputs';
import { QuestionType_Enum_ } from 'models';
import { MetricTypeIcon, MetricTypes } from 'Molecules/MetricTypeIcon';
import { Typography } from 'Tokens';
import { ESRSInputArticle } from 'containers/Esrs/EsrsUtilComponents';

export const ChoiceMetricInput = ({
  metric,
  companyReportingUnitId,
}: {
  metric: MetricsTableData['metric'];
  companyReportingUnitId?: string;
}) => {
  const options = useMemo(
    () =>
      metric.choiceSet?.choices.map((choice) => ({
        label: choice.title,
        value: choice.reference,
      })) ?? [],
    [metric]
  );

  const { onDatapointChange, dataPointPerYear, answer } = useGetDatapointValues(
    {
      metric,
    },
    companyReportingUnitId
  );

  const choices = useMemo(
    () => dataPointPerYear?.datapointChoices.map((choice) => choice.choiceRef),
    [dataPointPerYear?.datapointChoices]
  );

  const handleChangeAnswer = (newChoices: string[] | undefined, value?: string) => {
    return onDatapointChange({
      value: value ?? dataPointPerYear?.value ?? '',
      hasOptedOut: answer?.hasOptedOut ?? false,
      optOutReason: answer?.optOutReason ?? '',
      dp: dataPointPerYear ?? { timeframe: TimePeriodsEnums.year },
      choices: newChoices,
    });
  };

  const [inputValue, setInputValue] = useState(dataPointPerYear?.value ?? '');

  useEffect(() => {
    setInputValue(dataPointPerYear?.value ?? '');
  }, [dataPointPerYear?.value]);

  const debounceSave = useMemo(() => {
    return debounce((newChoices: string[] | undefined, value?: string) => {
      onDatapointChange({
        value: value ?? dataPointPerYear?.value ?? '',
        hasOptedOut: answer?.hasOptedOut ?? false,
        optOutReason: answer?.optOutReason ?? '',
        dp: dataPointPerYear ?? { timeframe: TimePeriodsEnums.year },
        choices: newChoices,
      });
    }, 1000);
  }, [onDatapointChange, answer, dataPointPerYear]);

  if (answer?.hasOptedOut) {
    return (
      <HStack py="8px">
        <MetricTypeIcon type={MetricTypes.optedout} />
        <Typography variant="body" color="text.hint" w="100%">
          Marked irrelevant
        </Typography>
      </HStack>
    );
  }

  return (
    <VStack alignItems="start" width="100%" spacing="2px">
      <MenuSelector
        isSingleSelect={metric.metricType === QuestionType_Enum_.SingleChoice_}
        options={options}
        handleChange={handleChangeAnswer}
        data={choices}
        isDisabled={answer?.hasOptedOut}
        extra={<ESRSInputArticle type="choices" />}
        isSearchable={true}
      />
      <AutoResizeTextarea
        variant="ghost"
        borderRadius="6px"
        lineHeight="20px"
        size="md"
        mb="4px"
        placeholder="Write your answer"
        value={inputValue ?? ''}
        onChange={({ target }) => {
          setInputValue(target.value);
          debounceSave(choices, target.value);
        }}
        onClick={(e) => e.stopPropagation()}
        isDisabled={answer?.hasOptedOut}
      />
    </VStack>
  );
};
